import { useState } from 'react';
import { isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { endpoints } from '../constants/endpoints';
import axios from '../lib/axios';
import type {
  CampaignFormValues,
  CampaignReservation,
  SentCampaign,
  WhatsappTemplate,
} from '../types/whatsapp';
import { csvFingerprint } from '../utils/parseBulkCsv';
import { uploadToS3 } from '../utils/uploadToS3';

interface SendInput {
  template: WhatsappTemplate;
  recipientsFile: File;
  recipientCount: number;
  mediaFile: File | null;
  values: CampaignFormValues;
}

async function buildForm(input: SendInput): Promise<FormData> {
  const { template, recipientsFile, mediaFile, values } = input;
  const form = new FormData();

  form.append('template_meta_id', template.meta_id);
  form.append('campaign_name', values.campaign_name);
  form.append('csv_fingerprint', await csvFingerprint(recipientsFile));
  if (values.send_at) form.append('send_at', values.send_at);
  if (values.button_suffix) form.append('button_suffix', values.button_suffix);
  if (values.coupon_code) form.append('coupon_code', values.coupon_code);
  if (mediaFile) form.append('header_media', mediaFile);

  return form;
}

export function useCampaignSend() {
  const [isSending, setIsSending] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [strandedCampaignId, setStrandedCampaignId] = useState<string | null>(
    null,
  );
  const [sent, setSent] = useState<SentCampaign | null>(null);

  const send = async (input: SendInput) => {
    if (isSending) return;

    setIsSending(true);
    setStrandedCampaignId(null);
    setUploadPercent(0);

    try {
      const reservation: CampaignReservation = await axios.post(
        endpoints.whatsapp.reserveCampaign,
        await buildForm(input),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      try {
        await uploadToS3(
          reservation.upload_url,
          reservation.upload_fields,
          input.recipientsFile,
          (event) =>
            setUploadPercent(
              event.total ? Math.round((event.loaded / event.total) * 100) : 0,
            ),
        );
      } catch {
        setStrandedCampaignId(reservation.campaign_id);
        return;
      }

      setSent({
        campaignId: reservation.campaign_id,
        campaignName: input.values.campaign_name,
        recipientCount: input.recipientCount,
        sendAt: input.values.send_at || 'as soon as the list is read',
      });
    } catch (error) {
      if (!isAxiosError(error)) {
        toast.error('Could not read the recipients file. Pick it again.');
      }
    } finally {
      setIsSending(false);
    }
  };

  const clear = () => {
    setSent(null);
    setStrandedCampaignId(null);
    setUploadPercent(0);
  };

  return { isSending, uploadPercent, strandedCampaignId, sent, send, clear };
}
